import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * useIdleTimeout Hook
 *
 * Tracks user activity and triggers callbacks for session timeout warnings and logout.
 *
 * SECURITY FEATURES:
 * - 15-minute idle timeout for HIPAA compliance
 * - Warning modal shown after warningDelayMs of inactivity; countdown runs for warningMs
 * - Automatic logout on timeout
 * - Activity tracking: mouse, keyboard, scroll, touch
 *
 * @param {Object} options
 * @param {Function} options.onWarning - Called when warning modal is shown
 * @param {Function} options.onTimeout - Called when session times out
 * @param {boolean} options.enabled - Whether timeout tracking is active
 * @param {number} options.timeoutMs - Total timeout in milliseconds (default: 15 minutes)
 * @param {number} options.warningDelayMs - Idle time before showing warning modal (default: 2 minutes)
 * @param {number} options.warningMs - Countdown duration in modal in milliseconds (default: 13 minutes)
 */

const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;       // 15 minutes
const DEFAULT_WARNING_DELAY_MS = 2 * 60 * 1000;  // show modal after 2 min idle
const DEFAULT_WARNING_MS = 13 * 60 * 1000;        // 13 min countdown in modal
const THROTTLE_MS = 1000;                         // Throttle activity events to 1 per second

export const useIdleTimeout = ({
    onWarning,
    onTimeout,
    enabled = true,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    warningDelayMs = DEFAULT_WARNING_DELAY_MS,
    warningMs = DEFAULT_WARNING_MS
}) => {
    const timeoutRef = useRef(null);
    const warningRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const [isWarningVisible, setIsWarningVisible] = useState(false);
    const isWarningVisibleRef = useRef(false);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const countdownRef = useRef(null);

    // Keep ref in sync so activity handler can read current value without effect re-running
    isWarningVisibleRef.current = isWarningVisible;

    /**
     * Clear all active timers
     */
    const clearTimers = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (warningRef.current) {
            clearTimeout(warningRef.current);
            warningRef.current = null;
        }
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    }, []);

    /**
     * Start countdown timer for warning modal
     */
    const startCountdown = useCallback(() => {
        const warningDurationSeconds = Math.floor(warningMs / 1000);
        setSecondsRemaining(warningDurationSeconds);
        
        countdownRef.current = setInterval(() => {
            setSecondsRemaining(prev => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current);
                    countdownRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [warningMs]);

    /**
     * Reset all timers - called on user activity
     */
    const resetTimer = useCallback(() => {
        if (!enabled) return;

        clearTimers();
        setIsWarningVisible(false);
        lastActivityRef.current = Date.now();

        // Show warning modal after warningDelayMs of inactivity; countdown runs for warningMs
        warningRef.current = setTimeout(() => {
            setIsWarningVisible(true);
            startCountdown();
            if (onWarning) {
                onWarning();
            }
        }, warningDelayMs);

        // Set timeout timer (fires at full timeout)
        timeoutRef.current = setTimeout(() => {
            setIsWarningVisible(false);
            if (onTimeout) {
                onTimeout();
            }
        }, timeoutMs);
    }, [enabled, timeoutMs, warningDelayMs, warningMs, onWarning, onTimeout, clearTimers, startCountdown]);

    /**
     * Handle user activity to stay logged in
     * Called when user clicks "Stay Logged In" on warning modal
     */
    const stayLoggedIn = useCallback(() => {
        resetTimer();
    }, [resetTimer]);

    /**
     * Get time remaining until timeout
     */
    const getTimeRemaining = useCallback(() => {
        const elapsed = Date.now() - lastActivityRef.current;
        const remaining = Math.max(0, timeoutMs - elapsed);
        return {
            total: remaining,
            minutes: Math.floor(remaining / 60000),
            seconds: Math.floor((remaining % 60000) / 1000)
        };
    }, [timeoutMs]);

    /**
     * Set up activity listeners and initialize timer
     */
    useEffect(() => {
        if (!enabled) {
            clearTimers();
            return;
        }

        // Events that indicate user activity
        const activityEvents = [
            'mousedown',
            'keydown',
            'scroll',
            'touchstart',
            'mousemove',
            'click'
        ];

        // Throttled activity handler
        let lastThrottleTime = 0;
        const handleActivity = () => {
            const now = Date.now();
            // Only reset if enough time has passed (throttle)
            if (now - lastThrottleTime > THROTTLE_MS) {
                lastThrottleTime = now;
                // Only reset if warning is not visible, or if it is, user needs to click button
                if (!isWarningVisibleRef.current) {
                    resetTimer();
                }
            }
        };

        // Add event listeners
        activityEvents.forEach(event => {
            window.addEventListener(event, handleActivity, { passive: true });
        });

        // Start the initial timer
        resetTimer();

        // Cleanup
        return () => {
            activityEvents.forEach(event => {
                window.removeEventListener(event, handleActivity);
            });
            clearTimers();
        };
        // Intentionally omit isWarningVisible: re-running when modal opens would clear countdown and logout timers
    }, [enabled, resetTimer, clearTimers]);

    return {
        resetTimer,
        stayLoggedIn,
        getTimeRemaining,
        isWarningVisible,
        secondsRemaining,
        clearTimers
    };
};

export default useIdleTimeout;

